import { formatPercent, formatUSD } from '@/lib/format-utils'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CeoTable } from './ceo-table'
import { ProjectedValue } from './projected-value'

const percent = (value) => formatPercent(value, 0, 1)
const money = (value) => formatUSD(value, 0, 0)

const WINDOW_DAYS = [30, 60, 90, 120, 180]
const OLD_SNAPSHOT = '该截止日留档早于本功能'

// 与业务群表同规则：预测 D90 < 97% 红，97%～103% 黄
const d90Tone = (value) => {
  if (value == null) return ''
  if (value < 0.97) return 'bg-destructive/10 text-destructive'
  if (value <= 1.03) return 'bg-warning/10 text-warning'
  return ''
}

const sourceText = (source) => {
  if (!source || (source.cell == null && source.pid == null)) return '-'
  return `本格 ${percent(source.cell)} / pid 级 ${percent(source.pid)}`
}

const detail = (window) =>
  [
    `最近 ${window.days} 天队列`,
    `队列数（格子 × 安装日）${window.cohorts}`,
    `平均天龄 ${window.avgAge == null ? '-' : `${window.avgAge.toFixed(0)} 天`}`,
    `未入模花费占比 ${percent(window.unmodeledCostShare)}`,
    `D90 ${percent(window.d90)}`,
    `预测 LTV 乐观 ${percent(window.ltvOptimistic)}`,
    `倍数来源 ${sourceText(window.multiplierSource)}`,
  ].join('\n')

const COLUMNS = ['花费', '已实现 ROAS', 'D90', 'D180', 'LTV（保守）', '未入模花费占比']

const projectedStatus = (window, key) => {
  if (window[key] == null) return false
  if (window.projected?.[key] != null) return window.projected[key]
  // Older results do not distinguish a fully mature D90 window from a mixed one.
  return key === 'd90' && window.days > 90 ? null : true
}

function WindowRow({ days, window }) {
  if (!window) {
    return (
      <TableRow>
        <TableCell className="whitespace-nowrap">最近 {days} 天</TableCell>
        <TableCell colSpan={COLUMNS.length} className="text-center text-muted-foreground">-</TableCell>
      </TableRow>
    )
  }
  const title = detail(window)
  const tone = d90Tone(window.d90)
  const cell = (value, className, projected) => (
    <TableCell title={title} className={cn('cursor-help text-right tabular-nums', tone, className)}>
      {projected === undefined ? value : <ProjectedValue projected={projected}>{value}</ProjectedValue>}
    </TableCell>
  )
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">最近 {days} 天</TableCell>
      {cell(money(window.cost))}
      {cell(percent(window.realized))}
      {cell(percent(window.d90), undefined, projectedStatus(window, 'd90'))}
      {cell(percent(window.d180), 'font-medium', projectedStatus(window, 'd180'))}
      {cell(percent(window.ltv), 'text-muted-foreground', projectedStatus(window, 'ltv'))}
      {cell(percent(window.unmodeledCostShare), 'text-muted-foreground')}
    </TableRow>
  )
}

function WindowsBody({ windows }) {
  if (!windows) return <p className="text-sm text-muted-foreground">{OLD_SNAPSHOT}，不显示该范围的回收预测。</p>
  return (
    <CeoTable>
      <TableHeader className="bg-muted">
        <TableRow>
          <TableHead className="whitespace-nowrap">窗口</TableHead>
          {COLUMNS.map((column) => (
            <TableHead key={column} className="text-right">{column}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {WINDOW_DAYS.map((days) => (
          <WindowRow key={days} days={days} window={windows.find((window) => window.days === days)} />
        ))}
      </TableBody>
    </CeoTable>
  )
}

/**
 * MON-45 按最近 N 天队列的回收预测；MON-58 转置：行 = 5 个窗口，Tabs 切范围。
 * scopes = [{ id, name, windows }]，第一项为当前范围，其后为下一层各行（MON-93）
 */
export default function LtvWindowsTable({ scopes, cohortForecast }) {
  const [current] = scopes
  const unavailable = ['live', 'unavailable'].includes(cohortForecast?.status)
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          按最近 N 天队列的回收预测
          {cohortForecast?.status === 'ready' && <Badge variant="outline" size="sm">每日批次 · 截至 {cohortForecast.asOf}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {cohortForecast?.status === 'excluded' ? (
          <Alert>
            <AlertDescription>当前范围没有可入模的格子。</AlertDescription>
          </Alert>
        ) : unavailable ? (
          <Alert>
            <AlertDescription>截至 {cohortForecast.asOf} 的预测批次尚未就绪，暂不显示回收预测。</AlertDescription>
          </Alert>
        ) : current.windows ? (
          <div className="flex flex-col gap-3">
            <Tabs defaultValue={current.id}>
              <div className="max-w-full overflow-x-auto">
                <TabsList>
                  {scopes.map((scope) => (
                    <TabsTrigger key={scope.id} value={scope.id}>{scope.name}</TabsTrigger>
                  ))}
                </TabsList>
              </div>
              {scopes.map((scope) => (
                <TabsContent key={scope.id} value={scope.id}>
                  <WindowsBody windows={scope.windows} />
                </TabsContent>
              ))}
            </Tabs>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{OLD_SNAPSHOT}，不显示按最近 N 天队列的回收预测。</p>
        )}
      </CardContent>
    </Card>
  )
}
